import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiCheck, FiSearch } from 'react-icons/fi';
import './OptionPicker.css';

export interface PickerOption {
  value: string;
  label: string;
  /** A quieter second line, e.g. a status. */
  description?: string;
  /** A figure at the end of the row. `countLabel` is what a screen reader
   *  hears in its place, since a bare number means nothing out of context. */
  count?: number;
  countLabel?: string;
  /** A rule is drawn wherever the group changes, to set "All" or "None"
   *  apart from the named options between them. */
  group?: string;
}

interface OptionPickerProps {
  options: PickerOption[];
  /** The current choice. Leave undefined for a list of actions, where nothing
   *  is "selected" and the check column is dropped. */
  value?: string | null;
  onSelect: (value: string) => void;
  onCancel: () => void;
  /** Accessible name for the panel, its search and its list. */
  label: string;
  /** Also print the label as a caption above the search. */
  showLabel?: boolean;
  searchPlaceholder: string;
  emptyMessage: string;
  className?: string;
}

/** Spelled out rather than left to the row's markup, which a screen reader
 *  would run together as "Acme3 sites". */
const getAccessibleName = (option: PickerOption): string => [
  option.label,
  option.description,
  option.countLabel ?? (option.count !== undefined ? String(option.count) : undefined),
].filter(Boolean).join(', ');

/** Case- and accent-insensitive, so "cafe" finds "Café". */
const normalize = (value: string) => value
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

/**
 * A searchable single-choice list for a popover: the search box keeps focus
 * and the arrow keys move a highlight through the list (the ARIA "list
 * autocomplete" combobox), so it scales from three options to three hundred
 * without a long row of chips. Opening, closing and positioning belong to the
 * caller.
 */
export default function OptionPicker({
  options,
  value,
  onSelect,
  onCancel,
  label,
  showLabel = false,
  searchPlaceholder,
  emptyMessage,
  className,
}: OptionPickerProps) {
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [query, setQuery] = useState('');
  // Open on the current choice so Enter keeps it and the arrows start from it.
  const [activeIndex, setActiveIndex] = useState(
    () => Math.max(0, options.findIndex((option) => option.value === value)),
  );

  const visibleOptions = useMemo(() => {
    const needle = normalize(query.trim());
    if (!needle) return options;
    return options.filter((option) => normalize(option.label).includes(needle));
  }, [options, query]);

  const active = visibleOptions.length > 0
    ? Math.min(activeIndex, visibleOptions.length - 1)
    : -1;
  const isSelectable = value !== undefined;

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (active < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    // Typing re-ranks the list, so the best match is the one Enter takes.
    setActiveIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const count = visibleOptions.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count) setActiveIndex((active + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count) setActiveIndex((active - 1 + count) % count);
        break;
      case 'Enter':
        event.preventDefault();
        if (active >= 0) onSelect(visibleOptions[active].value);
        break;
      case 'Escape':
        event.preventDefault();
        // Handled here, so a document-level Escape listener does not close
        // it a second time.
        event.stopPropagation();
        onCancel();
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`option-picker${className ? ` ${className}` : ''}`}
      role="dialog"
      aria-labelledby={showLabel ? labelId : undefined}
      aria-label={showLabel ? undefined : label}
    >
      {showLabel && <p className="option-picker__heading" id={labelId}>{label}</p>}

      <div className="option-picker__search">
        <FiSearch aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          className="option-picker__input"
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          placeholder={searchPlaceholder}
          aria-label={label}
          aria-expanded="true"
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-label={label}
        className="option-picker__list"
        // Keeps focus in the search box, so a click is not read as the
        // picker losing focus before it lands.
        onMouseDown={(event) => event.preventDefault()}
      >
        {visibleOptions.map((option, index) => {
          const isSelected = isSelectable && option.value === value;
          const startsGroup = index > 0 && option.group !== visibleOptions[index - 1].group;
          const classes = [
            'option-picker__option',
            index === active ? 'option-picker__option--active' : '',
            startsGroup ? 'option-picker__option--separated' : '',
          ].filter(Boolean).join(' ');

          return (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={isSelected}
              aria-label={getAccessibleName(option)}
              data-index={index}
              className={classes}
              onMouseMove={() => { if (index !== active) setActiveIndex(index); }}
              onClick={() => onSelect(option.value)}
            >
              {isSelectable && (
                <span className="option-picker__check" aria-hidden="true">
                  {isSelected && <FiCheck />}
                </span>
              )}
              <span className="option-picker__text">
                <span className="option-picker__label">{option.label}</span>
                {option.description && (
                  <span className="option-picker__description">{option.description}</span>
                )}
              </span>
              {option.count !== undefined && (
                <span className="option-picker__count">{option.count}</span>
              )}
            </li>
          );
        })}
      </ul>

      {visibleOptions.length === 0 && (
        <p className="option-picker__empty" role="status">{emptyMessage}</p>
      )}
    </div>
  );
}
